import { portableSpawn, portableSpawnSync } from "@skaile/workspaces/core";
import { Command } from "commander";
import pc from "picocolors";
import { logErr, logInfo, S } from "../helpers.ts";

interface Container {
  name: string;
  project: string;
  status: string;
  createdAt: string;
}

function parseContainers(raw: string): Container[] {
  return raw
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const [name = "", project = "", status = "", ...rest] = line.split("\t");
      return { name, project, status, createdAt: rest.join("\t") };
    });
}

function colorLine(line: string): string {
  if (/error|Error|ERROR|failed|Failed/.test(line)) return pc.red(line);
  if (/connector|MCP|tool/.test(line)) return pc.yellow(line);
  if (/rebuilt|registered|ready/.test(line)) return pc.green(line);
  if (/\[driver\]/.test(line)) return pc.cyan(line);
  if (/\[serve\]/.test(line)) return pc.dim(line);
  return line;
}

/** Deadline for the docker probe — an unreachable daemon must not hang the CLI. */
const DOCKER_PROBE_TIMEOUT_MS = 10_000;
/** Deadline for `docker ps` — local daemon query. */
const DOCKER_PS_TIMEOUT_MS = 15_000;
/** Deadline for a non-following `docker logs` read. */
const DOCKER_LOGS_TIMEOUT_MS = 60_000;

function dockerAvailable(): boolean {
  return (
    portableSpawnSync(["docker", "info"], { timeoutMs: DOCKER_PROBE_TIMEOUT_MS }).exitCode === 0
  );
}

function listContainers(): Container[] {
  const result = portableSpawnSync(
    [
      "docker",
      "ps",
      "--filter",
      "label=skaile.managed=true",
      "--format",
      '{{.Names}}\t{{.Label "skaile.project-slug"}}\t{{.Status}}\t{{.CreatedAt}}',
    ],
    { timeoutMs: DOCKER_PS_TIMEOUT_MS },
  );
  return parseContainers(result.stdout);
}

/**
 * Creates the `skaile logs` command.
 *
 * Tails logs from skaile-managed Docker agent containers. Supports `--follow`,
 * regex `--filter`, project slug filtering, and `--list` to enumerate running
 * containers.
 *
 * @returns Configured {@link Command} ready for `program.addCommand()`.
 * @docLink cli/commands/misc#logs
 */
export function makeLogsCommand(): Command {
  return new Command("logs")
    .description("Tail logs from skaile Docker agent containers")
    .option("--follow", "Follow log output (like tail -f)")
    .option("--lines <n>", "Number of lines to show", "50")
    .option("--filter <pattern>", "Filter log lines (regex)")
    .option("--project <slug>", "Filter by project slug")
    .option("--list", "List running skaile containers instead of showing logs")
    .action(
      async (opts: {
        follow?: boolean;
        lines: string;
        filter?: string;
        project?: string;
        list?: boolean;
      }) => {
        if (!dockerAvailable()) {
          logErr("Docker is not running or not installed");
          process.exitCode = 1;
          return;
        }

        const containers = listContainers();

        if (opts.list) {
          if (containers.length === 0) {
            logInfo("No skaile containers running");
            return;
          }
          console.log();
          console.log(
            `  ${S.dim("NAME".padEnd(30))}  ${S.dim("PROJECT".padEnd(20))}  ${S.dim("STATUS")}`,
          );
          console.log(`  ${S.dim("─".repeat(70))}`);
          for (const c of containers) {
            console.log(
              `  ${S.heading(c.name.padEnd(30))}  ${c.project.padEnd(20)}  ${S.dim(c.status)}`,
            );
          }
          console.log();
          return;
        }

        if (containers.length === 0) {
          logErr("No skaile containers running");
          process.exitCode = 1;
          return;
        }

        let target: Container | undefined;
        if (opts.project) {
          target = containers.find((c) => c.project === opts.project);
          if (!target) {
            logErr(`No container found for project: ${opts.project}`);
            process.exitCode = 1;
            return;
          }
        } else {
          // Most recently created is first in `docker ps` output
          target = containers[0];
        }

        const filterRe = opts.filter ? new RegExp(opts.filter) : null;

        const args = ["docker", "logs", "--tail", opts.lines];
        if (opts.follow) args.push("--follow");
        args.push(target.name);

        if (opts.follow) {
          const proc = portableSpawn(args);

          async function pipeStream(stream: ReadableStream<Uint8Array>) {
            const decoder = new TextDecoder();
            const reader = stream.getReader();
            let buf = "";
            try {
              while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                buf += decoder.decode(value, { stream: true });
                const lines = buf.split("\n");
                buf = lines.pop() ?? "";
                for (const line of lines) {
                  if (!filterRe || filterRe.test(line)) {
                    console.log(colorLine(line));
                  }
                }
              }
            } finally {
              reader.releaseLock();
            }
            if (buf && (!filterRe || filterRe.test(buf))) {
              console.log(colorLine(buf));
            }
          }

          await Promise.all([pipeStream(proc.stdout), pipeStream(proc.stderr)]);
        } else {
          const result = portableSpawnSync(args, { timeoutMs: DOCKER_LOGS_TIMEOUT_MS });
          const combined = result.stdout + result.stderr;
          const lines = combined.split("\n");
          for (const line of lines) {
            if (!line) continue;
            if (!filterRe || filterRe.test(line)) {
              console.log(colorLine(line));
            }
          }
        }
      },
    );
}
